import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import styled from 'styled-components';

import { getCmiDigestWatchlistRunCommunityDetail } from 'apis/CmiDigestWatchlistAPI';
import { getAIFeedbacks } from 'apis/FeedbackAPI';
import { ReviewsTable, toReviewRows } from 'components/Alerts/AlertsReviewsTable';
import { AI_FEEDBACK_FEATURE_TYPES } from 'components/Feedback/constants';
import Feedback from 'components/Feedback/Feedback';
import ErrorMessage from 'components/lib/ErrorMessage';
import LoaderRibbed from 'components/lib/LoaderRibbed';

import CommunitiesToWatchDetailsCard from './CommunitiesToWatchDetailsCard';
import CommunitiesToWatchFeeComparisonTable from './CommunitiesToWatchFeeComparisonTable';
import CommunitiesToWatchHeader from './CommunitiesToWatchHeader';
import CommunitiesToWatchIncentivesTable from './CommunitiesToWatchIncentivesTable';
import CommunitiesToWatchRateComparisonTable from './CommunitiesToWatchRateComparisonTable';
import CommunitiesToWatchSectionHeader from './CommunitiesToWatchSectionHeader';
import DigestSelector from './DigestSelector';

const Container = styled.div`
  width: 100%;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 10px 30px 30px 30px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 100px;
`;

const Section = styled.div``;

const DigestSelectorGap = styled.div`
  margin-bottom: -10px;
`;

const CommunitiesToWatchDetails = () => {
  const navigate = useNavigate();
  const { runId, runCommunityId } = useParams();
  const runIdNumber = Number(runId);
  const runCommunityIdNumber = Number(runCommunityId);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['cmi-digest-watchlist-community-detail', runIdNumber, runCommunityIdNumber],
    queryFn: () => getCmiDigestWatchlistRunCommunityDetail(runIdNumber, runCommunityIdNumber),
    enabled: !Number.isNaN(runIdNumber) && !Number.isNaN(runCommunityIdNumber),
    refetchOnWindowFocus: false,
  });

  const { data: feedback } = useQuery({
    queryKey: ['ai-feedbacks', 'cmi-digest-run-community', runCommunityIdNumber],
    queryFn: () => getAIFeedbacks({ cmi_digest_run_community_id: runCommunityIdNumber }),
    select: (feedbacks) => feedbacks?.[0] ?? null,
    enabled: !Number.isNaN(runCommunityIdNumber),
    refetchOnWindowFocus: false,
  });

  const handleSelectRun = (runCommunityIdToSelect: number) => {
    const target = data?.community_runs.find((run) => run.run_community_id === runCommunityIdToSelect);
    if (target && target.run_community_id !== runCommunityIdNumber) {
      navigate(`/communities-to-watch/${target.run_id}/communities/${target.run_community_id}`);
    }
  };

  return (
    <Container>
      <CommunitiesToWatchHeader>
        {!isLoading && !isError && data && (
          <Feedback
            subtitle={data.community_name}
            featureType={AI_FEEDBACK_FEATURE_TYPES.CMI_DIGEST_COMMUNITY}
            cmiDigestRunCommunity={runCommunityIdNumber}
            sourceUrl={window.location.href}
            existingFeedback={feedback}
          />
        )}
      </CommunitiesToWatchHeader>
      <Main>
        <ErrorMessage text="Failed to load community details. Please try again." active={isError} />

        {isLoading && (
          <LoaderContainer>
            <LoaderRibbed active />
          </LoaderContainer>
        )}

        {!isLoading && !isError && data && (
          <>
            {(data.community_runs ?? []).length > 0 && (
              <DigestSelectorGap>
                <DigestSelector
                  runs={data.community_runs.map((run) => ({
                    id: run.run_community_id,
                    frequency_label: run.frequency_label,
                    period_label: run.period_label,
                  }))}
                  selectedRunId={runCommunityIdNumber}
                  onSelect={handleSelectRun}
                />
              </DigestSelectorGap>
            )}

            <CommunitiesToWatchDetailsCard data={data} runId={runIdNumber} />

            {data.show_reviews && (data.reviews ?? []).length > 0 && (
              <Section>
                <CommunitiesToWatchSectionHeader icon="/icons/star-pink.svg" color="pink">
                  Notable Reviews
                </CommunitiesToWatchSectionHeader>
                <ReviewsTable rows={toReviewRows(data.reviews)} highlightNewestRow />
              </Section>
            )}

            {data.show_incentives && (data.incentives ?? []).length > 0 && (
              <Section>
                <CommunitiesToWatchSectionHeader icon="/icons/price-tag-mint.svg" color="mint">
                  Notable Competitor Incentives
                </CommunitiesToWatchSectionHeader>
                <CommunitiesToWatchIncentivesTable rows={data.incentives} />
              </Section>
            )}

            {data.show_incentives && (data.rate_comparison ?? []).length > 0 && (
              <Section>
                <CommunitiesToWatchSectionHeader icon="/icons/price-tag-blue.svg" color="blue">
                  Rate Comparison vs. Competitors
                </CommunitiesToWatchSectionHeader>
                <CommunitiesToWatchRateComparisonTable careTypes={data.rate_comparison} />
              </Section>
            )}

            {data.show_incentives && (data.fee_comparison ?? []).length > 0 && (
              <Section>
                <CommunitiesToWatchSectionHeader icon="/icons/document-purple.svg" color="lavender">
                  Fee Comparison vs. Competitors
                </CommunitiesToWatchSectionHeader>
                <CommunitiesToWatchFeeComparisonTable careTypes={data.fee_comparison} />
              </Section>
            )}
          </>
        )}
      </Main>
    </Container>
  );
};

export default CommunitiesToWatchDetails;
